import React, { useEffect, useState } from 'react';
import { ensureSessionHeaders } from '../../lib/serverSession';

interface ApprovedPayment {
  id: string;
  user_id: number;
  amount: number;
  currency: string | null;
  network: string | null;
  transaction_hash: string | null;
  status: string;
  created_at: string | null;
}

// Opens the Payment Manager in a new tab and deep-links straight to the exact
// payment via the hash lookup (comprehensive_hash_lookup accepts either the
// bank reference / transaction hash or the payment UUID as searchTerm).
const openPaymentInNewTab = (p: ApprovedPayment) => {
  const lookup = (p.transaction_hash && p.transaction_hash.trim()) || p.id;
  const url = `${window.location.origin}${window.location.pathname}#payments&lookup=${encodeURIComponent(lookup)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
};

interface RecipientUser {
  id: number;
  username: string | null;
  email: string | null;
  full_name?: string | null;
}

interface Props {
  userId: number;
  username: string;
  adminUserId?: number;
  onClose: () => void;
  onSuccess?: () => void;
}

const DEFAULT_REASON = 'Payment credited to the wrong account; reversing and reassigning to the correct recipient.';

export function ReversePaymentModal({ userId, username, adminUserId, onClose, onSuccess }: Props) {
  const [payments, setPayments] = useState<ApprovedPayment[]>([]);
  const [users, setUsers] = useState<RecipientUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [selectedPaymentId, setSelectedPaymentId] = useState<string>('');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientId, setRecipientId] = useState<number | null>(null);
  const [newAmount, setNewAmount] = useState('');
  const [reason, setReason] = useState(DEFAULT_REASON);
  const [ackChecked, setAckChecked] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setLoadError('');
        const headers = { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) };
        const [payRes, userRes] = await Promise.all([
          fetch('/api/admin/master-audit', {
            method: 'POST', headers,
            body: JSON.stringify({ operation: 'load_user_approved_payments', data: { userId } })
          }),
          fetch('/api/admin/payment-operations', {
            method: 'POST', headers,
            body: JSON.stringify({ operation: 'load_all_users', data: {} })
          })
        ]);
        const payJson = await payRes.json();
        const userJson = await userRes.json();
        if (!payRes.ok) throw new Error(payJson.error || 'Failed to load approved payments');
        if (!userRes.ok) throw new Error(userJson.error || 'Failed to load users');
        if (!cancelled) {
          setPayments(payJson.data || []);
          setUsers((userJson.data && userJson.data.users) || []);
        }
      } catch (e: any) {
        if (!cancelled) setLoadError(e?.message || 'Failed to load reverse-payment data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const selectedPayment = payments.find(p => p.id === selectedPaymentId) || null;
  const canSubmit = Boolean(
    selectedPayment &&
    recipientId &&
    recipientId !== userId &&
    reason.trim().length >= 10 &&
    ackChecked &&
    confirmText.trim().toUpperCase() === 'REVERSE' &&
    !submitting
  );

  const handleSubmit = async () => {
    if (!selectedPayment || !recipientId) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const headers = { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) };
      const body: any = {
        paymentId: selectedPayment.id,
        correctUserId: recipientId,
        reassignReason: reason.trim()
      };
      if (adminUserId) body.adminUserId = adminUserId;
      if (newAmount.trim() !== '') body.newAmount = newAmount.trim();
      const res = await fetch('/api/admin/reassign-payment', {
        method: 'POST', headers, body: JSON.stringify(body)
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to reverse payment');
      alert(json.message || 'Payment reversed and reassigned successfully.');
      onSuccess?.();
      onClose();
    } catch (e: any) {
      setSubmitError(e?.message || 'Failed to reverse payment');
    } finally {
      setSubmitting(false);
    }
  };

  const fmtAmt = (n: number) => `$${Number(n || 0).toFixed(2)}`;
  const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleDateString() : '—');
  const filteredUsers = users.filter(u => {
    const q = recipientSearch.trim().toLowerCase();
    if (!q) return true;
    if (/^\d+$/.test(q)) return u.id.toString() === q;
    return (u.username || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  }).slice(0, 50);

  const labelStyle: React.CSSProperties = { display: 'block', color: '#9CA3AF', fontSize: '12px', fontWeight: 600, marginBottom: '6px' };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 10px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '6px', color: '#FFFFFF', fontSize: '13px' };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: '#1F2937', border: '2px solid #EF4444', borderRadius: '8px', maxWidth: '760px', width: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #374151', position: 'sticky', top: 0, backgroundColor: '#1F2937', zIndex: 1 }}>
          <div style={{ color: '#EF4444', fontWeight: 700, fontSize: '15px' }}>
            🔄 Reverse Payment — {username} (User ID {userId})
          </div>
          <button onClick={onClose} style={{ backgroundColor: '#374151', color: '#FFFFFF', border: 'none', borderRadius: '4px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}>
            ✕ Close
          </button>
        </div>

        <div style={{ padding: '16px 20px' }}>
          {loading ? (
            <div style={{ color: '#FFFFFF' }}>Loading approved payments…</div>
          ) : loadError ? (
            <div style={{ color: '#FCA5A5' }}>{loadError}</div>
          ) : payments.length === 0 ? (
            <div style={{ color: '#FCD34D' }}>This user has no approved payments to reverse.</div>
          ) : (
            <>
              <label style={labelStyle}>1. Select the approved payment to reverse *</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px', maxHeight: '180px', overflowY: 'auto' }}>
                {payments.map(p => (
                  <label key={p.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '8px 10px', backgroundColor: selectedPaymentId === p.id ? '#3F2222' : '#111827', border: `1px solid ${selectedPaymentId === p.id ? '#EF4444' : '#374151'}`, borderRadius: '6px', cursor: 'pointer' }}>
                    <input type="radio" name="rev-payment" checked={selectedPaymentId === p.id} onChange={() => setSelectedPaymentId(p.id)} style={{ marginTop: '3px' }} />
                    <span style={{ color: '#FFFFFF', fontSize: '13px', flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 700 }}>{fmtAmt(p.amount)}</span> {p.currency || ''} · {p.network || '—'} · {fmtDate(p.created_at)}
                      <span style={{ display: 'block', marginTop: '3px', color: '#9CA3AF', fontSize: '12px' }}>
                        Reference:{' '}
                        <span
                          role="link"
                          tabIndex={0}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openPaymentInNewTab(p); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); openPaymentInNewTab(p); } }}
                          title="Open this exact payment in a new tab"
                          style={{ color: '#60A5FA', fontFamily: 'monospace', wordBreak: 'break-all', textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          {p.transaction_hash || p.id} ↗
                        </span>
                      </span>
                    </span>
                  </label>
                ))}
              </div>

              <label style={labelStyle}>2. Correct recipient — funds &amp; shares move here *</label>
              <input style={{ ...inputStyle, marginBottom: '6px' }} placeholder="Filter by User ID, Email, or Username…" value={recipientSearch} onChange={(e) => setRecipientSearch(e.target.value)} />
              <select style={{ ...inputStyle, marginBottom: '4px' }} value={recipientId ?? ''} onChange={(e) => setRecipientId(e.target.value ? parseInt(e.target.value) : null)}>
                <option value="">Select the correct user…</option>
                {filteredUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.username} ({u.email}) — ID {u.id}</option>
                ))}
              </select>
              {recipientId === userId && (
                <div style={{ color: '#FCA5A5', fontSize: '12px', marginBottom: '8px' }}>Correct recipient must differ from the current user.</div>
              )}

              <label style={{ ...labelStyle, marginTop: '12px' }}>3. Corrected amount (optional — leave blank to keep original)</label>
              <input style={{ ...inputStyle, marginBottom: '12px' }} type="number" min="0" step="0.01" placeholder={selectedPayment ? fmtAmt(selectedPayment.amount) : 'Original amount'} value={newAmount} onChange={(e) => setNewAmount(e.target.value)} />

              <label style={labelStyle}>4. Reason (min 10 characters) *</label>
              <textarea style={{ ...inputStyle, minHeight: '64px', marginBottom: '12px', resize: 'vertical' }} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#FCA5A5', fontSize: '12px', marginBottom: '10px' }}>
                <input type="checkbox" checked={ackChecked} onChange={(e) => setAckChecked(e.target.checked)} style={{ marginTop: '2px' }} />
                <span>I understand this reverses the deposit (cancels shares &amp; claws back commissions) and re-applies it to the correct user. This cannot be undone.</span>
              </label>
              <label style={labelStyle}>Type REVERSE to confirm *</label>
              <input style={{ ...inputStyle, marginBottom: '12px' }} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="REVERSE" />

              {submitError && <div style={{ color: '#FCA5A5', fontSize: '12px', marginBottom: '10px' }}>{submitError}</div>}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={onClose} style={{ backgroundColor: '#374151', color: '#FFFFFF', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}>Cancel</button>
                <button onClick={handleSubmit} disabled={!canSubmit} style={{ backgroundColor: canSubmit ? '#EF4444' : '#4B5563', color: '#FFFFFF', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: canSubmit ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: 700, opacity: canSubmit ? 1 : 0.6 }}>
                  {submitting ? 'Reversing…' : '🔄 Reverse & Reassign'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

